/** Site picker (home), Add site (studio), Account, Privacy notice. */
import { useState, type FormEvent } from 'react';
import { Layout } from '../components/Layout.js';
import { ChefHat, Dialog, ErrorBox, Loading } from '../components/ui.js';
import { useT } from '../i18n.js';
import { Link, useRouter } from '../router.js';
import { useAsync, useBackend, useSession, useToast } from '../state.js';
import { LangSwitch, SiteFooter } from '../components/Footer.js';
import { errorText } from './Login.js';
import { relativeTime } from '../lib/format.js';
import type { SiteSummary } from '../backend/types.js';

export function Sites() {
  const { t, lang } = useT();
  const { me } = useSession();
  const [adding, setAdding] = useState(false);
  if (!me) return null;
  return (
    <Layout sidebar={false}>
      <div className="title-row">
        <div>
          <h1>{t('yourSites')}</h1>
          {me.user.studio && <p className="desc muted">Hotpot Studio</p>}
        </div>
        {me.user.studio && (
          <div className="actions">
            <button className="btn" onClick={() => setAdding(true)}>
              {t('addSite')}+
            </button>
          </div>
        )}
      </div>
      {me.user.studio && <SetupCard />}
      {me.sites.length === 0 ? (
        <div className="empty">
          <ChefHat />
          <p>{t('noSites')}</p>
        </div>
      ) : (
        <ul className="rows">
          {me.sites.map((s) => (
            <SiteRow key={s.id} site={s} lang={lang} />
          ))}
        </ul>
      )}
      {adding && <AddSite onClose={() => setAdding(false)} />}
    </Layout>
  );
}

/** Studio only: connect GitHub (one click, creates the GitHub App) and choose which repositories it may edit. */
function SetupCard() {
  const { t } = useT();
  const backend = useBackend();
  const status = useAsync(() => backend.setupStatus(), []);
  const gh = status.data?.github;
  if (!gh || (gh.configured && !gh.installUrl)) return null;
  return (
    <div className="notice pink" style={{ marginBottom: 28, fontSize: 18 }}>
      {gh.configured ? (
        <>
          {t('githubConnected')}{' '}
          <a href={gh.installUrl!} target="_blank" rel="noopener noreferrer">
            {t('githubRepos')} ↗
          </a>
        </>
      ) : (
        <>
          <p style={{ margin: '0 0 12px' }}>{t('githubConnectText')}</p>
          <a className="btn" href="/api/setup/github">
            {t('githubConnect')}
          </a>
        </>
      )}
    </div>
  );
}

function SiteRow({ site, lang }: { site: SiteSummary; lang: string }) {
  const { t } = useT();
  const backend = useBackend();
  const last = useAsync(async () => {
    const handle = await backend.openSite(site.id);
    return (await handle.storage.history({ limit: 1 }))[0];
  }, [site.id]);
  const roleLabel = { studio: t('roleStudio'), owner: t('roleOwner'), editor: t('roleEditor') }[site.role];
  const [editing, setEditing] = useState(false);
  return (
    <li className="row">
      <span className="thumb" style={{ display: 'grid', placeContent: 'center', fontFamily: 'Bandit Condensed', fontSize: 30, lineHeight: 1 }} aria-hidden="true">
        {site.name.slice(0, 1)}
      </span>
      <Link className="main" to={`/s/${site.id}`}>
        <span className="t">{site.name}</span>
        <span className="s">
          {roleLabel}
          {site.repo ? ` · ${site.repo}${site.branch && site.branch !== 'main' ? ` (${site.branch})` : ''}` : ''}
          {site.drafts ? ` · ${t('preview').toLowerCase()}` : ''}
          {last.data ? ` · ${t('lastEdit')}: ${relativeTime(last.data.date, t, lang)} (${last.data.author.name})` : ''}
        </span>
      </Link>
      <span className="meta">
        {site.role === 'studio' && (
          <button className="link-btn" onClick={() => setEditing(true)}>
            {t('edit')}
          </button>
        )}
        {site.url && (
          <a href={site.url} target="_blank" rel="noopener noreferrer">
            {t('openSite')}
          </a>
        )}
      </span>
      {editing && <EditSite site={site} onClose={() => setEditing(false)} />}
    </li>
  );
}

/** Cloudflare Pages' address for a branch, assuming the Pages project is named like the repository. */
function guessPreviewUrl(repo: string, branch: string): string {
  const project = repo.split('/')[1]?.toLowerCase().replace(/[^a-z0-9-]/g, '-');
  const alias = branch.toLowerCase().replace(/[^a-z0-9-]/g, '-');
  return project && alias ? `https://${alias}.${project}.pages.dev` : '';
}

function PreviewFields({ form, setForm, repo }: { form: { previewBranch: string; previewUrl: string }; setForm: (f: { previewBranch: string; previewUrl: string }) => void; repo: string }) {
  const { t } = useT();
  return (
    <>
      <label>
        {t('previewBranch')}
        <input className="input" value={form.previewBranch} onChange={(e) => setForm({ ...form, previewBranch: e.target.value })} placeholder="cms-preview" />
        <span className="hint">{t('previewBranchHint')}</span>
      </label>
      {form.previewBranch && (
        <label>
          {t('previewUrl')}
          <input
            className="input"
            type="url"
            value={form.previewUrl}
            onChange={(e) => setForm({ ...form, previewUrl: e.target.value })}
            placeholder={guessPreviewUrl(repo, form.previewBranch) || 'https://'}
          />
          <span className="hint">{t('previewUrlHint')}</span>
        </label>
      )}
    </>
  );
}

function EditSite({ site, onClose }: { site: SiteSummary; onClose: () => void }) {
  const { t } = useT();
  const backend = useBackend();
  const { refresh } = useSession();
  const [form, setForm] = useState({ name: site.name, branch: site.branch ?? 'main', url: site.url, previewBranch: site.previewBranch ?? '', previewUrl: site.previewUrl ?? '' });
  const [error, setError] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await backend.updateSite(site.id, { ...form, previewUrl: form.previewBranch ? form.previewUrl || guessPreviewUrl(site.repo ?? '', form.previewBranch) : '' });
      await refresh();
      onClose();
    } catch (err) {
      setError(errorText(err, t));
    }
  };
  return (
    <Dialog
      title={site.name}
      onClose={onClose}
      buttons={
        <>
          <button className="link-btn" onClick={onClose}>
            {t('cancel')}
          </button>
          <button className="btn" form="edit-site">
            {t('save')}
          </button>
        </>
      }
    >
      <form id="edit-site" className="form-grid" onSubmit={submit}>
        <label>
          {t('siteName')}
          <input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label>
          {t('branch')}
          <input className="input" required value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} />
          <span className="hint">{t('branchHint')}</span>
        </label>
        <label>
          {t('liveUrl')}
          <input className="input" type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
        </label>
        <PreviewFields form={form} setForm={(f) => setForm({ ...form, ...f })} repo={site.repo ?? ''} />
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
}

function AddSite({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const backend = useBackend();
  const { refresh } = useSession();
  const { navigate } = useRouter();
  const [form, setForm] = useState({ id: '', name: '', repo: 'Christianoo/', branch: 'main', url: 'https://', previewBranch: 'cms-preview', previewUrl: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await backend.addSite({ ...form, previewUrl: form.previewBranch ? form.previewUrl || guessPreviewUrl(form.repo, form.previewBranch) : '' });
      await refresh();
      navigate(`/s/${form.id}/people`);
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('addSite')}
      onClose={onClose}
      wide
      buttons={
        <>
          <button className="link-btn" onClick={onClose}>
            {t('cancel')}
          </button>
          <button className="btn" form="add-site" disabled={busy}>
            {t('addSite')}
          </button>
        </>
      }
    >
      <ol className="steps">
        <li>{t('addSiteSteps1')}</li>
        <li>{t('addSiteSteps2')}</li>
        <li>{t('addSiteSteps3')}</li>
        <li>{t('addSiteSteps4')}</li>
      </ol>
      <form id="add-site" className="form-grid" onSubmit={submit}>
        <label>
          {t('siteName')}
          <input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, id: form.id || '' })} />
        </label>
        <label>
          {t('siteId')}
          <input className="input" required pattern="[a-z0-9-]{2,40}" value={form.id} onChange={set('id')} placeholder="smyk" />
        </label>
        <label>
          {t('repo')}
          <input className="input" required value={form.repo} onChange={set('repo')} />
        </label>
        <label>
          {t('branch')}
          <input className="input" required value={form.branch} onChange={set('branch')} />
        </label>
        <label>
          {t('liveUrl')}
          <input className="input" type="url" value={form.url} onChange={set('url')} />
        </label>
        <PreviewFields form={form} setForm={(f) => setForm({ ...form, ...f })} repo={form.repo} />
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
}

export function Account() {
  const { t } = useT();
  const backend = useBackend();
  const { me, setMe } = useSession();
  const toast = useToast();
  const [name, setName] = useState(me?.user.name ?? '');
  if (!me) return null;
  return (
    <Layout sidebar={false} narrow>
      <div className="title-row">
        <h1>{t('account')}</h1>
      </div>
      <form
        className="form-grid"
        onSubmit={async (e) => {
          e.preventDefault();
          setMe(await backend.updateName(name));
          toast(t('saved'));
        }}
      >
        <label>
          {t('email')}
          <input className="input" value={me.user.email} readOnly disabled />
        </label>
        <label>
          {t('yourName')}
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
          <span className="hint">{t('yourNameHint')}</span>
        </label>
        <div>
          <button className="btn">{t('save')}</button>
        </div>
      </form>
    </Layout>
  );
}

export function Privacy() {
  const { t, lang } = useT();
  const backend = useBackend();
  const { me } = useSession();
  const op = useAsync(() => backend.legal(), []);
  const o = op.data;
  const operator = o ? (
    <>
      {o.name || 'Hotpot Studio'}
      {o.email ? (
        <>
          , <a href={`mailto:${o.email}`}>{o.email}</a>
        </>
      ) : null}
    </>
  ) : (
    'Hotpot Studio'
  );
  const body =
    lang === 'cs' ? (
      <div className="prose">
        <p>Hotpot CMS je nástroj, ve kterém klienti Hotpot Studia upravují obsah svých webů.</p>
        <h2>Správce</h2>
        <p>{operator}</p>
        <h2>Jaké údaje zpracováváme a proč</h2>
        <ul>
          <li><strong>E-mail a jméno</strong> – abychom vás mohli přihlásit a pozvat a aby bylo vidět, kdo co upravil. Právní základ: plnění smlouvy s vaší organizací a oprávněný zájem.</li>
          <li><strong>Záznamy o přihlášení</strong> (čas, IP adresa, prohlížeč) – kvůli bezpečnosti a omezení zneužití. Mažeme je nejpozději po 90 dnech.</li>
          <li><strong>Historie úprav</strong> – každá uložená změna se ukládá do repozitáře webu na GitHubu (GitHub, Inc., USA; přenos na základě standardních smluvních doložek / EU–US Data Privacy Framework) s vaším jménem a e-mailem jako autorem. Je součástí historie webu.</li>
        </ul>
        <h2>Cookies</h2>
        <p>Používáme jen jednu nezbytnou cookie s přihlášením (<code>hp_session</code>, 30 dní). Žádnou analytiku ani sledování, proto nepotřebujeme souhlas s cookies. Písma i všechny soubory načítáme z vlastního serveru.</p>
        <h2>Zpracovatelé</h2>
        <p>Cloudflare (hosting a databáze), GitHub (úložiště obsahu), Resend (odesílání přihlašovacích e-mailů).</p>
        <h2>Vaše práva</h2>
        <p>Máte právo na přístup, opravu, výmaz, omezení zpracování, přenositelnost a námitku. Napište správci. Stížnost můžete podat u Úřadu pro ochranu osobních údajů (uoou.gov.cz).</p>
      </div>
    ) : (
      <div className="prose">
        <p>Hotpot CMS is the tool Hotpot Studio’s clients use to edit their websites.</p>
        <h2>Controller</h2>
        <p>{operator}</p>
        <h2>What we process and why</h2>
        <ul>
          <li><strong>Email and name</strong>: to log you in, to invite you, and to show who changed what. Legal basis: performance of the contract with your organisation, and legitimate interest.</li>
          <li><strong>Login records</strong> (time, IP address, browser): for security and to limit abuse. Deleted after 90 days at the latest.</li>
          <li><strong>Edit history</strong>: every save is stored in the site’s GitHub repository (GitHub, Inc., USA; transfer under standard contractual clauses / EU–US Data Privacy Framework) with your name and email as the author. It is part of the site’s history.</li>
        </ul>
        <h2>Cookies</h2>
        <p>Only one strictly necessary login cookie (<code>hp_session</code>, 30 days). No analytics or tracking, so no cookie consent is needed. Fonts and all files load from our own server.</p>
        <h2>Processors</h2>
        <p>Cloudflare (hosting and database), GitHub (content storage), Resend (login emails).</p>
        <h2>Your rights</h2>
        <p>Access, rectification, erasure, restriction, portability and objection: write to the controller. You can complain to the Czech data protection authority (ÚOOÚ, uoou.gov.cz).</p>
      </div>
    );
  const content = (
    <>
      <div className="title-row">
        <h1>{t('privacyTitle')}</h1>
      </div>
      {op.error ? <ErrorBox error={op.error} /> : op.loading ? <Loading /> : body}
    </>
  );
  if (me) return <Layout sidebar={false}>{content}</Layout>;
  return (
    <div className="auth">
      <div className="top">
        <Link to="/login" style={{ background: 'none' }}>
          <img src={`${import.meta.env.BASE_URL}logo.svg`} alt="HOTPOT CMS" width={304} height={68} style={{ height: '100%' }} />
        </Link>
        <LangSwitch />
      </div>
      <main className="card" style={{ width: 'min(820px, calc(100% - 32px))' }}>
        {content}
      </main>
      <SiteFooter />
    </div>
  );
}

export function NotFound() {
  const { t } = useT();
  return (
    <Layout sidebar={false}>
      <div className="empty">
        <p className="big-word">404</p>
        <p>{t('notFound')}</p>
        <Link className="btn" to="/">
          {t('allSites')}
        </Link>
      </div>
    </Layout>
  );
}
